import { CodeBlock, TabbedCodeBlock } from '../../components/developers/CodeBlock';

export default function SdkQuickstart() {
  return (
    <article className="prose prose-slate max-w-none">
      <h1>SDK Quickstart</h1>
      <p className="lead">
        Integrate Opair into your TypeScript application in minutes with our official SDK.
      </p>

      <h2>Installation</h2>
      <TabbedCodeBlock
        tabs={[
          { label: 'npm', code: 'npm install @opair/sdk', language: 'bash' },
          { label: 'pnpm', code: 'pnpm add @opair/sdk', language: 'bash' },
          { label: 'yarn', code: 'yarn add @opair/sdk', language: 'bash' },
        ]}
      />

      <h2>Quick Start</h2>
      <p>Initialize the client and start making requests:</p>

      <CodeBlock
        title="src/opair.ts"
        language="typescript"
        showLineNumbers
        code={`import { OpairClient } from '@opair/sdk';

// Initialize with API key
const opair = new OpairClient({
  apiKey: process.env.OPAIR_API_KEY!,
  baseUrl: 'https://api.opair.example.com',
});

// Or with Personal Access Token
const opairPat = new OpairClient({
  accessToken: process.env.OPAIR_PAT!,
  baseUrl: 'https://api.opair.example.com',
});

export { opair };`}
      />

      <h2>Core Operations</h2>

      <h3>Evaluate a Policy Decision</h3>
      <CodeBlock
        language="typescript"
        code={`import { opair } from './opair';

const decision = await opair.policies.evaluate({
  policyId: 'content-moderation-v1',
  input: {
    content: 'User generated content here',
    userId: 'user-123',
    context: { channel: 'chat' },
  },
});

if (decision.allowed) {
  console.log('Content approved');
} else {
  console.log('Content blocked:', decision.reasons);
}`}
      />

      <h3>Policy Scanning (Detector Framework)</h3>
      <p>The SDK provides access to all detector types for content scanning:</p>
      <CodeBlock
        language="typescript"
        code={`import { opair } from './opair';

// Scan content through the policy engine
const scanResult = await opair.policies.scan({
  content: 'User message to analyze',
  detectors: ['bias', 'toxicity', 'pii', 'prompt_injection', 'ip_leakage', 'rag_poisoning'],
});

// Each detection includes type, confidence, and span
for (const detection of scanResult.detections) {
  console.log(detection.type, detection.confidence, detection.severity);
  // e.g. "pii_email", 0.95, "high"
}

// Apply masking
const masked = await opair.policies.mask({
  content: 'Contact me at user@example.com',
  detectors: ['pii'],
});
console.log(masked.content); // "Contact me at [EMAIL]"`}
      />

      <h3>OpenAI-Compatible Adapter</h3>
      <p>
        Drop-in replacement for the OpenAI SDK — route all LLM calls through Opair for governance:
      </p>
      <CodeBlock
        language="typescript"
        showLineNumbers
        code={`import { OpairClient } from '@opair/sdk';

// Create an OpenAI-compatible client routed through Opair Gateway
const client = new OpairClient({
  apiKey: process.env.OPAIR_API_KEY!,
  baseUrl: 'https://gateway.opair.example.com',
});

// Use the same API as OpenAI SDK
const completion = await client.chat.completions.create({
  model: 'gpt-4',
  messages: [
    { role: 'system', content: 'You are a helpful assistant.' },
    { role: 'user', content: 'Explain AI governance.' },
  ],
});

// All requests are automatically:
// - Routed through configured provider
// - Scanned by active policies (PII, toxicity, prompt injection, etc.)
// - Logged to audit trail
// - Rate-limited and cost-tracked
console.log(completion.choices[0].message.content);`}
      />

      <div className="not-prose my-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-semibold text-blue-900">Migrating from OpenAI SDK</h4>
        <p className="text-blue-800 text-sm mt-1">
          Replace <code>baseURL</code> with your Opair Gateway URL and use your Opair API key. All{' '}
          <code>chat.completions</code>, <code>embeddings</code>, and <code>models</code> calls work
          identically — Opair transparently proxies to your configured provider.
        </p>
      </div>

      <h3>Provider Configuration</h3>
      <CodeBlock
        language="typescript"
        code={`// List available providers
const providers = await opair.providers.list();
for (const p of providers.items) {
  console.log(p.name, p.type, p.status);
}

// Get provider model catalog
const models = await opair.providers.listModels('provider-123');
console.log(models); // [{ id: 'gpt-4', name: 'GPT-4', ... }]

// Test provider connectivity
const testResult = await opair.providers.test('provider-123');
console.log(testResult.status); // "connected"`}
      />

      <h3>Workload Management</h3>
      <CodeBlock
        language="typescript"
        code={`// Create a workload
const workload = await opair.workloads.create({
  name: 'customer-chatbot',
  slug: 'customer-chatbot',
  description: 'Customer support chatbot',
  sensitivity: 'high',
});

// Manage API keys for the workload
const key = await opair.apiKeys.create(workload.id, {
  label: 'Production key',
});
console.log(key.apiKey); // Raw key shown once

// Rotate API key
await opair.apiKeys.rotate(workload.id, key.id);`}
      />

      <h3>List Policies</h3>
      <CodeBlock
        language="typescript"
        code={`const policies = await opair.policies.list({
  status: 'active',
  limit: 10,
});

for (const policy of policies.items) {
  console.log(policy.name, policy.version);
}`}
      />

      <h3>Workflow Execution</h3>
      <CodeBlock
        language="typescript"
        code={`// Trigger a workflow
const execution = await opair.workflows.trigger('wf-compliance-check', {
  input: { policyId: 'policy-123' },
});
console.log(execution.id, execution.status);

// Check execution status
const status = await opair.workflows.getExecution('wf-compliance-check', execution.id);
console.log(status.steps); // Step-by-step execution log`}
      />

      <h3>Audit &amp; Compliance</h3>
      <CodeBlock
        language="typescript"
        code={`// Query audit events
const events = await opair.audit.list({
  action: 'policy.evaluation',
  from: '2026-01-01T00:00:00Z',
  limit: 50,
});

// Generate compliance report
const report = await opair.compliance.generateReport({
  framework: 'SOC2',
  startDate: '2026-01-01',
  endDate: '2026-03-31',
});
console.log(report.id, report.status);`}
      />

      <h2>Error Handling</h2>
      <CodeBlock
        language="typescript"
        code={`import { OpairClient, OpairError } from '@opair/sdk';

try {
  const result = await opair.policies.evaluate({ ... });
} catch (error) {
  if (error instanceof OpairError) {
    console.error('API Error:', error.code, error.message);
    if (error.code === 'RATE_LIMITED') {
      // Handle rate limiting — retry after delay
    }
  }
  throw error;
}`}
      />

      <h2>Event Handling (Real-Time)</h2>
      <CodeBlock
        language="typescript"
        code={`// Subscribe to real-time events via SSE
const stream = opair.events.subscribe({
  types: ['policy.violation', 'security.alert'],
});

stream.on('event', (event) => {
  console.log(event.type, event.data);
});

stream.on('error', (err) => {
  console.error('Stream error:', err);
});

// Clean up
stream.close();`}
      />

      <h2>TypeScript Support</h2>
      <p>The SDK includes full TypeScript definitions. Import types directly:</p>

      <CodeBlock
        language="typescript"
        code={`import type {
  Policy,
  PolicyDecision,
  EvaluationInput,
  AuditLogEntry,
  Workload,
  Provider,
  ScanResult,
  Detection,
} from '@opair/sdk';

function processDecision(decision: PolicyDecision): void {
  // Full type safety and autocompletion
}`}
      />

      <h2>Configuration Options</h2>
      <CodeBlock
        language="typescript"
        code={`const opair = new OpairClient({
  // Required
  apiKey: process.env.OPAIR_API_KEY!,

  // Optional
  baseUrl: 'https://api.opair.example.com',
  timeout: 30000, // Request timeout in ms
  retries: 3,     // Number of retry attempts

  // Hooks
  onRequest: (config) => {
    console.log('Request:', config.method, config.url);
  },
  onResponse: (response) => {
    console.log('Response:', response.status);
  },
});`}
      />

      <div className="not-prose my-8 p-4 bg-green-50 border border-green-200 rounded-lg">
        <h3 className="text-lg font-semibold text-green-900 mb-2">SDK Features</h3>
        <ul className="text-green-800 space-y-1 text-sm">
          <li>Full TypeScript support with type inference</li>
          <li>Automatic retry with exponential backoff</li>
          <li>Request/response interceptors</li>
          <li>Streaming support for large responses</li>
          <li>Tree-shakable for minimal bundle size</li>
          <li>OpenAI-compatible adapter for transparent LLM governance</li>
          <li>Real-time event subscriptions via SSE</li>
        </ul>
      </div>
    </article>
  );
}
